import ReactMarkdown from "react-markdown";
import remarkBreaks from "remark-breaks";
import remarkGfm from "remark-gfm";

type MarkdownPreviewProps = {
  content: string;
  emptyText?: string;
  compact?: boolean;
};

export function MarkdownPreview({ content, emptyText = "Chưa có nội dung để xem trước.", compact = false }: MarkdownPreviewProps) {
  const normalized = normalizeMarkdownForNotes(content);

  if (!normalized) {
    return <p className="text-sm text-muted">{emptyText}</p>;
  }

  return (
    // Be Vietnam Pro has no real italic face; the global `font-synthesis: none`
    // (src/index.css) blocks the browser from faking the slant, so `*nghiêng*`
    // rendered upright. Re-enable synthetic weight+style just for rendered Markdown.
    <div
      className={compact ? "space-y-2 text-sm leading-6 text-slate-700" : "space-y-3 text-sm leading-6 text-slate-700"}
      style={{ fontSynthesis: "weight style" }}
    >
      <ReactMarkdown
        remarkPlugins={[remarkGfm, remarkBreaks]}
        components={{
          h1: ({ node: _node, ...props }) => <h1 className="text-base font-normal leading-6 text-ink" {...props} />,
          h2: ({ node: _node, ...props }) => <h2 className="text-sm font-normal leading-6 text-ink" {...props} />,
          h3: ({ node: _node, ...props }) => <h3 className="text-sm font-normal leading-6 text-ink" {...props} />,
          p: ({ node: _node, ...props }) => <p className="leading-6" {...props} />,
          em: ({ node: _node, ...props }) => <em className="italic" {...props} />,
          strong: ({ node: _node, ...props }) => <strong className="font-semibold" {...props} />,
          a: ({ node: _node, ...props }) => <a className="text-brand underline underline-offset-2" target="_blank" rel="noreferrer" {...props} />,
          ul: ({ node: _node, ...props }) => <ul className="ml-5 list-disc space-y-1" {...props} />,
          ol: ({ node: _node, ...props }) => <ol className="ml-5 list-decimal space-y-1" {...props} />,
          li: ({ node: _node, ...props }) => <li className="pl-1" {...props} />,
          blockquote: ({ node: _node, ...props }) => (
            <blockquote className="border-l-2 border-blue-200 bg-blue-50 px-3 py-2 text-slate-700" {...props} />
          ),
          code: ({ node: _node, className, children, ...props }) => (
            <code className={`${className ?? ""} rounded bg-slate-100 px-1.5 py-0.5 text-[0.85em] text-slate-800`} {...props}>
              {children}
            </code>
          ),
          pre: ({ node: _node, ...props }) => <pre className="overflow-x-auto rounded-lg bg-slate-900 p-3 text-xs leading-5 text-slate-50" {...props} />,
          table: ({ node: _node, ...props }) => <table className="w-full border-collapse text-left text-xs" {...props} />,
          th: ({ node: _node, ...props }) => <th className="border border-line bg-slate-50 px-2 py-1 font-normal" {...props} />,
          td: ({ node: _node, ...props }) => <td className="border border-line px-2 py-1" {...props} />
        }}
      >
        {normalized}
      </ReactMarkdown>
    </div>
  );
}

function normalizeMarkdownForNotes(content: string) {
  return content
    .trim()
    .replace(/^(#{1,6})([^\s#])/gm, "$1 $2");
}
